import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useExpenseCategories } from '@/hooks/useExpenseCategories';
import { useInvoiceSettings } from '@/hooks/useInvoiceSettings';
import { useMultiTenant } from '@/hooks/useMultiTenant';
import { getLeafCategories } from '@/lib/utils/categoryFilters';

/**
 * C-4 (R-COMPRAS.10, D-C.9 a D-C.13) — categorias de despesa para os trilhos de COMPRA.
 *
 * - Fonte (D-C.10, filtro duro COM fallback): folhas ativas com is_purchase_operation = true;
 *   com menos de MIN_FLAGGED marcadas no tenant, cai para TODAS as folhas ativas.
 * - Grupo 5 do DRE (movimentações não-resultado) fica fora sempre, como no CategorySelect.
 * - Ordem (D-C.11): uso DERIVADO em leitura — títulos vivos de compra por categoria, desc;
 *   empate = alfabético. Sem contador armazenado, sem RPC nova.
 * - Default (D-C.13, E-C4.1): invoice_settings.default_expense_category_id, se estiver na lista;
 *   senão a mais usada com uso > 0 — TAMBÉM no fallback (o ranking só conta títulos de compra,
 *   então o topo é sempre categoria de compra genuína); sem uso nenhum = sem pré-seleção.
 */

/** Abaixo disso o filtro por flag deixaria o Select quase vazio — vale o fallback integral. */
const MIN_FLAGGED = 2;
/** Teto de linhas do PostgREST; o ranking olha os títulos de compra mais recentes. */
const USAGE_SAMPLE = 1000;
/** Nome literal da categoria que get_or_create_internal_use_category cria (âncora 4.01.04). */
const INTERNAL_USE_CATEGORY_NAME = 'Saída para uso interno';

export interface PurchaseExpenseCategoryOption {
  id: string;
  name: string;
  description: string | null;
  /** Títulos vivos de compra nesta categoria (amostra dos USAGE_SAMPLE mais recentes). */
  usage: number;
}

export function usePurchaseExpenseCategories() {
  const { companyId } = useMultiTenant();
  const { categories, isLoading: loadingCategories } = useExpenseCategories();
  const { settings, isLoading: loadingSettings } = useInvoiceSettings();

  const usageQuery = useQuery({
    queryKey: ['purchase-expense-category-usage', companyId],
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Record<string, number>> => {
      // Select leve: só a coluna contada. RLS de accounts_payable já isola o tenant; o filtro
      // explícito por company_id e deleted_at é regra da casa.
      const { data, error } = await supabase
        .from('accounts_payable')
        .select('expense_category_id')
        .eq('company_id', companyId as string)
        .is('deleted_at', null)
        .not('purchase_id', 'is', null)
        .not('expense_category_id', 'is', null)
        .order('created_at', { ascending: false })
        .limit(USAGE_SAMPLE);
      if (error) throw error;

      const counts: Record<string, number> = {};
      for (const row of data ?? []) {
        const id = row.expense_category_id as string | null;
        if (id) counts[id] = (counts[id] ?? 0) + 1;
      }
      return counts;
    },
  });

  const result = useMemo(() => {
    const counts = usageQuery.data ?? {};

    const leaves = getLeafCategories(categories).filter(
      // dre_structure vem do join do useExpenseCategories e não está no tipo ExpenseCategory
      (cat) => !((cat as any).dre_structure?.code ?? '').startsWith('5'),
    );

    // TODO types.ts regen: is_purchase_operation (C-3) ainda não está nos tipos gerados
    const flagged = leaves.filter((cat) => (cat as any).is_purchase_operation === true);
    const isFallback = flagged.length < MIN_FLAGGED;
    const base = isFallback ? leaves : flagged;

    const options: PurchaseExpenseCategoryOption[] = base
      .map((cat) => ({
        id: cat.id,
        name: cat.category_name,
        description: (cat as any).description ?? null,
        usage: counts[cat.id] ?? 0,
      }))
      .sort((a, b) => b.usage - a.usage || a.name.localeCompare(b.name, 'pt-BR'));

    // TODO types.ts regen: default_expense_category_id existe em invoice_settings, fora do tipo local
    const settingsDefault = ((settings as any)?.default_expense_category_id ?? null) as string | null;

    let defaultId = '';
    if (settingsDefault && options.some((o) => o.id === settingsDefault)) {
      defaultId = settingsDefault;
    } else if (options.length > 0 && options[0].usage > 0) {
      defaultId = options[0].id;
    }

    // C-6 (D-C.8): default do campo "Categoria de uso interno" = a categoria-âncora do motor
    // (get_or_create_internal_use_category), SE estiver na lista. O front nunca a cria.
    const internalUseDefaultId =
      options.find((o) => o.name === INTERNAL_USE_CATEGORY_NAME)?.id ?? '';

    return { options, defaultId, internalUseDefaultId, isFallback };
  }, [categories, usageQuery.data, settings]);

  return {
    ...result,
    isLoading: loadingCategories || loadingSettings || usageQuery.isLoading,
  };
}

/**
 * C-6 (D-DRE.1 front, D-C.5/D-C.8) — estado dos DOIS campos de categoria dos trilhos de compra:
 * Categoria de Despesa (revenda; sempre visível) e Categoria de uso interno (visível só quando
 * algum item da compra é 'internal_use'). Mesma lista e mesmo ranking para os dois.
 *
 * - autoDefault (criação): pré-seleciona UMA vez cada campo enquanto o operador não mexeu.
 *   Na EDIÇÃO passa false: a tela mostra o que está gravado, sem trocar valor em silêncio.
 * - categoryPayload: chaves prontas para o motor — create_purchase_atomic na RAIZ do payload,
 *   update_purchase_atomic DENTRO de header. expense_category_id vai sempre;
 *   internal_use_category_id só quando o campo está visível (chave presente aplica).
 */
export function usePurchaseCategorySelection(opts: {
  hasInternalUse: boolean;
  autoDefault?: boolean;
  initialExpenseCategoryId?: string | null;
  initialInternalUseCategoryId?: string | null;
}) {
  const { hasInternalUse, autoDefault = true } = opts;
  const { options, defaultId, internalUseDefaultId, isLoading } = usePurchaseExpenseCategories();

  const [expenseCategoryId, setExpenseCategoryId] = useState(opts.initialExpenseCategoryId ?? '');
  const [internalUseCategoryId, setInternalUseCategoryId] = useState(
    opts.initialInternalUseCategoryId ?? '',
  );
  const [expenseTouched, setExpenseTouched] = useState(false);
  const [internalUseTouched, setInternalUseTouched] = useState(false);

  useEffect(() => {
    if (!autoDefault || expenseTouched || expenseCategoryId || !defaultId) return;
    setExpenseCategoryId(defaultId);
  }, [autoDefault, expenseTouched, expenseCategoryId, defaultId]);

  useEffect(() => {
    if (!autoDefault || !hasInternalUse || internalUseTouched || internalUseCategoryId || !internalUseDefaultId) return;
    setInternalUseCategoryId(internalUseDefaultId);
  }, [autoDefault, hasInternalUse, internalUseTouched, internalUseCategoryId, internalUseDefaultId]);

  const selectExpenseCategory = useCallback((id: string) => {
    setExpenseTouched(true);
    setExpenseCategoryId(id);
  }, []);

  const selectInternalUseCategory = useCallback((id: string) => {
    setInternalUseTouched(true);
    setInternalUseCategoryId(id);
  }, []);

  /** Edição: carrega o que está gravado na compra e desliga a pré-seleção dos dois campos. */
  const hydrate = useCallback((expenseId: string | null | undefined, internalUseId: string | null | undefined) => {
    setExpenseCategoryId(expenseId ?? '');
    setInternalUseCategoryId(internalUseId ?? '');
    setExpenseTouched(true);
    setInternalUseTouched(true);
  }, []);

  const categoryPayload: Record<string, string | null> = {
    expense_category_id: expenseCategoryId || null,
    ...(hasInternalUse ? { internal_use_category_id: internalUseCategoryId || null } : {}),
  };

  return {
    options,
    isLoading,
    expenseCategoryId,
    internalUseCategoryId,
    selectExpenseCategory,
    selectInternalUseCategory,
    hydrate,
    categoryPayload,
  };
}
